import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_ORDER_SERVICE } from '@/api/constant';
import Breadcrumb from '@/components/common/Breadcrumb';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import FormField from '@/components/UI/FormField';
import { showToastNotification } from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import ErrorMessage from '../../components/common/ErrorMessage';
import { errorTypes } from '@/utility/validation';
import BtnLoader from '@/components/UI/loaders/BtnLoader';

interface CouponProps {
  id?: string;
  name: string
  code: string;
  type: string;
  description: string;
  discountValue: number;
  maxDiscount: number;
  startDate: string;
  expirationDate: string;
  minimumValue: number;
}

const AddProductCoupon = () => {
  const navigate = useNavigate();
  const { code } = useParams();
  const couponCode = code;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dateError, setDateError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<CouponProps>({
    defaultValues: {
      id: '',
      name: '',
      code: '',
      type: '',
      description: '',
      maxDiscount: 0,
      startDate: '',
      expirationDate: '',
      minimumValue: 0,
    },
  });
  const id = watch('id');
  const [StartDate, endDate] = [watch("startDate"), watch("expirationDate")];
  const codeText = watch('code');

  useEffect(() => {
    if (codeText && /\s/.test(codeText)) {
      setError('code', {
        type: 'manual',
        message: 'Coupon code cannot contain spaces.',
      });
    } else {
      clearErrors('code');
    }
  }, [codeText, setError, clearErrors]);

  useEffect(() => {
    if (!StartDate || !endDate) {
      setDateError(null);
      return;
    }

    const start = new Date(StartDate);
    const end = new Date(endDate);

    if (end < start) {
      setDateError('End date cannot be earlier than start date.');
    } else if (end.getTime() === start.getTime()) {
      setDateError('Start date and end date cannot be the same.');
    } else {
      setDateError(null);
    }
  }, [StartDate, endDate]);

  const fetchCouponInfo = async () => {
    try {
      const PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.coupons}/${couponCode}`;
      const res = await callApi(PATH, { method: 'GET' });
      reset({ ...res });
    } catch (error) {
      console.log('error is', error);
    }
  };

  useEffect(() => {
    if (couponCode) fetchCouponInfo();
  }, [couponCode]);

  const onSubmit = async (data: CouponProps) => {
    if (dateError) {
      showToastNotification('danger', dateError)
      return
    }
    setIsLoading(true);
    try {
      let PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.coupons}`;
      if (couponCode) PATH = `${PATH}/${id}`;
      await callApi(PATH, { method: couponCode ? 'PUT' : 'POST', data });
      setIsLoading(false);
      console.log('data is', data);
      navigate(RoutesLink.coupons);
    } catch (error: unknown) {
      const err = error as { message?: string };
      setIsLoading(false);
      if (err.message) showToastNotification('danger', err.message);
      console.log('error in creating coupon', error);
    }
  };

  const discountType = watch('type');

  const breadcrumb = [
    { label: 'Coupons', href: RoutesLink.coupons },
    { label: couponCode ? 'Edit Coupon' : 'Add Coupon' },
  ];
  return (
    <Layout>
      <Breadcrumb items={breadcrumb} />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Discount Code */}
        <Card>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
            <div>
              <FormField
                label="Title"
                type="text"
                placeholder="eg: special code for eid"
                {...register('name', {
                  ...errorTypes.required('name'),
                  minLength: {
                    value: 4,
                    message: 'Coupon name must be at least 4 characters long.',
                  },
                })}
              />
            </div>
            <div>
              <FormField
                label="Coupon Code"
                type="text"
                placeholder="Enter coupon code"
                disabled={!!code}
                {...register('code', {
                  ...errorTypes.required('code'),
                  minLength: {
                    value: 4,
                    message: 'Coupon code must be at least 4 characters long.',
                  },
                  pattern: {
                    value: /^[^\s]+$/,
                    message: 'Coupon code cannot contain spaces.',
                  },
                })}
              />
              <p className="text-xs text-gray-500 mt-2">
                Customer have to enter this coupon at checkout
              </p>
              {couponCode && (
                <ErrorMessage
                  message="You can not edit the coupon code"
                  className="text-yellow-500! text-xs"
                />
              )}
              <ErrorMessage message={errors.code?.message} />
            </div>
          </div>
          <div className="mt-3">
            <FormField
              label="Description"
              type="textarea"
              placeholder="Enter description for code"
              rows={5}
              {...register('description', {
                ...errorTypes.required('description'),
              })}
            />
            <ErrorMessage message={errors.description?.message} />
          </div>
        </Card>
        {/* Discount Value */}
        <Card>
          <div className="space-y-6">
            <h2 className="text-lg font-semibold">Discount Value</h2>
            {/* Discount Type + Value */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
              <div>
                <FormField
                  label="Discount Type"
                  type="select"
                  options={[
                    { value: 'percentage', label: 'Percentage' },
                    { value: 'flat', label: 'Fixed Amount' },
                  ]}
                  {...register('type', { ...errorTypes.required('type') })}
                />
                <ErrorMessage message={errors.type?.message} />
              </div>
              <div>
                <FormField
                  label="Value"
                  type="number"
                  placeholder="20"
                  suffix={discountType === 'percentage' ? '%' : undefined}
                  prefix={discountType === 'flat' ? 'BD' : undefined}
                  min={1}
                  {...register('discountValue', {
                    valueAsNumber: true,
                    ...errorTypes.required('discount value'),
                  })}
                />
                <ErrorMessage message={errors.discountValue?.message} />
              </div>
            </div>
            {/* Discount Type + Value */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
              <FormField
                label="Max Discount"
                type="number"
                placeholder="Enter Max Discount"
                min={1}
                {...register('maxDiscount', { valueAsNumber: true })}
              />
              <FormField
                label="Min Purchase Value"
                type="number"
                placeholder="20"
                prefix="BD"
                min={0}
                step="0.01"
                {...register('minimumValue', { valueAsNumber: true })}
              />
            </div>
          </div>
        </Card>
        <Card>
          <div className="space-y-6">
            <h2 className="text-lg font-semibold mb-4">Active dates</h2>

            <div>
              <div className="grid grid-cols-2 gap-2">
                <FormField
                  label="Start Date"
                  type="date"
                  defaultValue={StartDate?.split('T')[0] || ''}
                  onChange={(e) => {
                    const date = e.target.value;
                    const time = StartDate?.split('T')[1] || '00:00';
                    setValue('startDate', `${date}T${time}`);
                  }}
                  className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px]"
                />
                <FormField
                  label="Start Time"
                  type="time"
                  defaultValue={
                    StartDate?.split('T')[1]?.slice(0, 5) || '00:00'
                  }
                  value={
                    StartDate
                      ? StartDate.slice(11, 16) // "04:05"
                      : '00:00'
                  }
                  onChange={(e) => {
                    const time = e.target.value;
                    const date =
                      StartDate?.split('T')[0] ||
                      new Date().toISOString().split('T')[0];
                    setValue('startDate', `${date}T${time}`);
                  }}
                  className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px]"
                />
              </div>
            </div>
            <div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <FormField
                  label="End Date"
                  type="date"
                  defaultValue={endDate?.split('T')[0] || ''}
                  onChange={(e) => {
                    const date = e.target.value;
                    const time =
                      endDate?.split('T')[1] || '23:59';
                    setValue('expirationDate', `${date}T${time}`);
                  }}
                  className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px]"
                />

                {/* Time */}
                <FormField
                  label="End Time"
                  type="time"
                  defaultValue={
                    endDate?.split('T')[1]?.slice(0, 5) || '23:59'
                  }
                  value={
                    endDate
                      ? endDate.slice(11, 16) // "04:05"
                      : '00:00'
                  }
                  onChange={(e) => {
                    const time = e.target.value;
                    const date =
                      endDate?.split('T')[0] ||
                      new Date().toISOString().split('T')[0];
                    setValue('expirationDate', `${date}T${time}`);
                  }}
                  className="w-full border border-light-40 rounded-2xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary h-[42px]"
                />
              </div>
              <ErrorMessage message={dateError as string} />
            </div>
          </div>
        </Card>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => { reset(), navigate(RoutesLink.coupons) }}
            type="button"
            className="btn-secondary"
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary">
            {isLoading ? <BtnLoader /> : 'Save'}
          </button>
        </div>
      </form>
    </Layout>
  );
};

export default AddProductCoupon;
